/**
 * **The terrain mechanics overlay's pieces and labels** (`projects/terrain-mechanics-overlay.md`
 * Phase 3). The board layer in `pixiCombatBoard.ts` outlines each piece and labels it; this module
 * is the pure half: which pieces, what each label says, and when a label drops to its compact form.
 *
 * Pieces come from `@lancer-vtt/domain`'s `terrainMechanicsRegions` by property set (ruling 1's
 * basis A), the same answer `lvtt encounter terrain regions` prints.
 */
import { parseSquareKey } from "@lancer-vtt/space";
import { terrainMechanicsRegions, terrainRegionLabel, type TerrainMap } from "@lancer-vtt/domain";
import { CELL } from "./combatCells";

/** One outlined, labelled piece. */
export type TerrainMechanicsPiece = {
  label: string;
  compact: string;
  cells: string[];
  labelCell: string;
  /** How many piece cells run unbroken through the label cell's row: the room the label has. */
  runCells: number;
};

/** The label's font size in screen px; labels hold this size at every zoom. */
export const MECHANICS_LABEL_FONT = 10;
/** JetBrains Mono's advance is 0.6 em, so a label's width follows from its length. */
const CHAR_WIDTH = 0.6 * MECHANICS_LABEL_FONT;
/** Clear space either side of a label, in screen px. */
const LABEL_MARGIN = 3;

export function terrainMechanicsPieces(terrain: TerrainMap, board: { width: number; height: number }): TerrainMechanicsPiece[] {
  return terrainMechanicsRegions(terrain, board, { by: "set" }).map((r) => {
    const cells = new Set(r.cells);
    const { x, y } = parseSquareKey(r.labelCell);
    let x0 = x;
    let x1 = x;
    while (cells.has(`${x0 - 1},${y}`)) x0--;
    while (cells.has(`${x1 + 1},${y}`)) x1++;
    return {
      label: terrainRegionLabel(r.properties),
      compact: terrainRegionLabel(r.properties, { compact: true }),
      cells: r.cells,
      labelCell: r.labelCell,
      runCells: x1 - x0 + 1,
    };
  });
}

/** Screen px a label of `text` takes, margins included. */
export function mechanicsLabelWidth(text: string): number {
  return text.length * CHAR_WIDTH + 2 * LABEL_MARGIN;
}

/**
 * The text a piece's label shows at zoom `scale`: the full label when it fits across the piece's run
 * on screen, the compact form otherwise. A piece too small even for the compact form still shows it,
 * so no piece is ever silent.
 */
export function mechanicsLabelText(piece: TerrainMechanicsPiece, scale: number): string {
  return mechanicsLabelWidth(piece.label) <= piece.runCells * CELL * scale ? piece.label : piece.compact;
}

/**
 * The pieces whose cells include `key`, in their given order: what the Inspect tool outlines for the
 * hovered cell (`combatApp/useInspectTool.ts`). Under the set basis a painted cell is in exactly one
 * piece and open ground in none; a `by: "property"` layering can put one cell in several.
 */
export function piecesContaining<P extends { cells: readonly string[] }>(pieces: readonly P[], key: string): P[] {
  return pieces.filter((p) => p.cells.includes(key));
}
